import { Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { ZenLoginComponent} from '../../../../_modules/zen-login/_components/zen-login/zen-login.component';
import { Router } from '@angular/router';
/**
 * Service to monitor and handle version updates via Angular Service Worker.
 * When a new version is available, a user-friendly toast is shown and
 * a reload is triggered with important localStorage keys preserved.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  private hashKey = 'lastReloadHashPair';
  private pollIntervalMs = 10 * 60 * 1000; //  Check every 10 minutes
  newVersionAvailable = false;

  constructor(private swUpdate: SwUpdate, private router: Router) {
    this.initServiceWorkerUpdateCheck();
  }

  /**
   * Initializes update checking logic:
   * - Subscribes to SW lifecycle events
   * - Starts periodic polling to manually check for updates
   */
  initServiceWorkerUpdateCheck(): void {
    if (!this.swUpdate.isEnabled) {
      console.warn('[VersionCheck] Angular Service Worker is not enabled.');
      return;
    }

    // Angular will emit version update events if a new version is detected or ready
    this.swUpdate.versionUpdates.subscribe(event => {
      switch (event.type) {
        case 'VERSION_DETECTED':
          console.log('[SW] New version detected, downloading...');
          // This will trigger VERSION_READY when download completes
          break;

        case 'VERSION_INSTALLATION_FAILED':
          console.error('[SW] Installation has failed:', event);
          break;

        case 'VERSION_READY':
          // New version has finished downloading, wait for user to reload
          this.handleVersionReady(event as VersionReadyEvent);
          break;
      }
    });

    // Start polling manually to ensure timely detection (e.g., every 10 minutes)
    this.startPollingForUpdates();
  }

  /**
   * Called when a new version is downloaded and ready to activate.
   * Marks it as available so a banner or toast can notify the user.
   */
  private handleVersionReady(versionEvent: VersionReadyEvent): void {
    const reloadPair = {
      current: versionEvent.currentVersion.hash,
      new: versionEvent.latestVersion.hash,
    };

    const lastReloadJson = localStorage.getItem(this.hashKey);
    const lastReload = lastReloadJson ? JSON.parse(lastReloadJson) : null;

    // Skip if we've already handled this exact update
    if (
      lastReload &&
      lastReload.current === reloadPair.current &&
      lastReload.new === reloadPair.new
    ) {
      console.warn('[VersionCheck] Already handled this version update — skipping.');
      return;
    }

    localStorage.setItem(this.hashKey, JSON.stringify(reloadPair));


    if (this.isOnLoginComponent()) {
      console.log('[VersionCheck] User not logged in — auto refreshing.');
      this.forceHardReloadPreservingKeys(null); // auto-refresh for login page or anonymous users
    } else {
      console.log('[VersionCheck] New version ready — user is logged in, will show UI banner.');
      this.newVersionAvailable = true; // show banner to the user
    }

  }

  /**
   * Polls every N ms for updates using Angular's checkForUpdate.
   * Does NOT activate the update — just lets Angular handle lifecycle.
   */
  private startPollingForUpdates(): void {
    setInterval(() => {
      this.swUpdate.checkForUpdate().then(hasUpdate => {
        if (hasUpdate) {
          console.log('[SW] Update found, will trigger VERSION_READY event');
        }
        // Let Angular handle the rest: VERSION_READY will fire if applicable
      }).catch(err => {
        console.error('[SW] checkForUpdate error:', err);
      });
    }, this.pollIntervalMs);
  }

  /**
   * Called when user clicks banner or toast.
   * Triggers hard reload while preserving important localStorage keys.
   */
  forceHardReloadPreservingKeys(event: MouseEvent): void {
    // If we have an event, prevent default and stop propagation
    if (event) {
      event.preventDefault(); // This is critical
      event.stopPropagation(); // Optional, adds more safety
    }

    const keysToKeep = ['advisorTosSigned', 'currentUser', 'zenStore', 'customerTacApproved', this.hashKey];
    const preservedValues = new Map<string, string | null>();

    keysToKeep.forEach(key => {
      preservedValues.set(key, localStorage.getItem(key));
    });
    localStorage.clear();

    preservedValues.forEach((value, key) => {
      if (value !== null) {
        localStorage.setItem(key, value);
      }
    });

    // Add a cache-busting query param to force reload from Service Worker
    const url = new URL(window.location.href);
    url.searchParams.set('reload', Date.now().toString());

    this.newVersionAvailable = false;
    window.location.href = url.toString();

  }

  private isOnLoginComponent(): boolean {
    const currentRoute = this.router.routerState?.snapshot?.root;

    let route = currentRoute;
    while (route.firstChild) {
      route = route.firstChild;
    }

    return route?.component === ZenLoginComponent;
  }

}
